import './signInPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { PHONE_NUMBER_PATTERN } from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import { ReactNode, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { NumberInput } from '../../components/NumberInput/NumberInput';
import useApiStore from '../../store/apiStore/useApiStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { phoneNumber: parameterPhoneNumber } = useParameterStore();
  const {
    primaryPhoneNumber: pinDeliveredPhoneNumber,
    deliverPin,
    deliverPinFailedMessage,
  } = usePinStore();
  const { t } = useTranslation();

  const error = new URLSearchParams(window.location.search).get('error');
  const [errorDismissed, setErrorDismissed] = useState(false);

  const form = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      primaryPhoneNumberValue: toPhoneString(
        pinDeliveredPhoneNumber || parameterPhoneNumber || '',
      ),
    },
    delayError: 200,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
  };

  const handleRedirect = (): void => {
    usePinStore.setState({ deliverPinFailedMessage: null });
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { primaryPhoneNumberValue } = values;

    await deliverPin({
      deliverPinLocation: DeliverPinLocation.SIGN_IN,
      phone: primaryPhoneNumberValue,
      purpose: AuthType.ACCOUNT_SIGN_IN_USING_PIN,
    });

    const { deliverPinSuccess } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigate('/sign-in/pin-verify');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page sign-in-page">
        <Header
          trailing={
            <Link
              data-testid={locator.pages.signIn.header.signupLink}
              to="/sign-up"
              onClick={handleRedirect}
            >
              {t('SignInPage.signup')}
            </Link>
          }
          showLogo
          splitLayout
        />

        <main>
          <figure>
            <img
              alt={t('imageAlts.heroReference')}
              src={heroReferenceMessage}
            />
          </figure>
          <form
            data-testid={locator.pages.signIn.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.signIn.form.h1}>
                {t('SignInPage.title')}
              </h1>
              <h2 data-testid={locator.pages.signIn.form.h2}>
                {t('SignInPage.subtitle')}
              </h2>

              {error && !errorDismissed && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.signIn.alert.info}
                    severity="info"
                    onClose={(): void => {
                      setErrorDismissed(true);
                    }}
                  >
                    {t(`errors.parameter.${error}`)}
                  </Alert>
                </div>
              )}

              {deliverPinFailedMessage && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.signIn.alert.error}
                    severity="error"
                  >
                    <Trans
                      components={{
                        signup: (
                          <Link
                            data-testid={locator.pages.signIn.alert.signupLink}
                            to="/sign-up"
                            onClick={handleRedirect}
                          />
                        ),
                      }}
                    >
                      {deliverPinFailedMessage}
                    </Trans>
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <NumberInput
                  {...register(
                    'primaryPhoneNumberValue',
                    phoneNumberValidation,
                  )}
                  autoComplete="tel-national"
                  data-testid={locator.pages.signIn.form.phoneInput}
                  error={errors.primaryPhoneNumberValue}
                  errorTestId={locator.pages.signIn.form.phoneInputError}
                  label={t('SignInPage.phoneNumber')}
                  phone
                />
              </div>

              <Button
                data-testid={locator.pages.signIn.form.submitButton}
                loader={isLoading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('SignInPage.continue')}
              </Button>

              <p className="form-sign-up">
                {t('SignInPage.noAccount')}&nbsp;
                <Link
                  data-testid={locator.pages.signIn.form.signupLink}
                  to="/sign-up"
                  onClick={handleRedirect}
                >
                  {t('SignInPage.signup')}
                </Link>
              </p>
            </div>
            <footer data-testid={locator.pages.signIn.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'SignInPage';
